import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ValueAccessorDirective } from '../../directives/value-accessor.directive';
import { NgFor, NgIf } from '@angular/common';
import { FileUpload } from 'primeng/fileupload';
import { FileFromServer, FileRemoveResponse, FileUploadResponse } from 'src/app/core/models/files';
import { ApiService } from 'src/app/core/services/api.service';
import { FilesService } from 'src/app/core/services/files.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DEFAULT_MAX_UPLOAD_FILE_SIZE_MB,
  downloadBlobAsFile,
  filterFilesByMaxSize,
  getMaxUploadFileSizeErrorMessage,
} from '../../helpers/file-utils';
import { finalize } from 'rxjs';

@Component({
  selector: 'pa-uploadfile-button',
  templateUrl: './pa-uploadfile-button.component.html',
  styleUrls: ['./pa-uploadfile-button.component.scss'],
  standalone: true,
  imports: [FileUpload, ButtonModule, NgIf, NgFor],
  hostDirectives: [ValueAccessorDirective],
})
export class PaUploadfileButtonComponent implements OnInit {
  @Input() acceptedFiles: string =
    '.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls,.csv,.ppt,.pptx,.odp,.ods,.odt,.rtf,.txt,.vsd,.xml,.zip,.rar,.7z';
  @Input() maxFileSizeMB: number = DEFAULT_MAX_UPLOAD_FILE_SIZE_MB;

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  private readonly api = inject(ApiService);
  private readonly destroy = inject(DestroyRef);
  public readonly valueAccessor = inject(ValueAccessorDirective<FileFromServer[]>);
  fileFromServer: FileFromServer[] = [];

  // Отслеживание уже загруженных файлов для предотвращения повторной загрузки
  private uploadedFiles: Set<string> = new Set();

  constructor(
    private readonly filesService: FilesService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.valueAccessor.control.value && this.valueAccessor.control.value.length > 0) {
      this.fileFromServer = [...this.valueAccessor.control.value];
      this.fileFromServer.forEach((file) => {
        this.uploadedFiles.add(`${file.original_name}.${file.ext}`);
      });
    }
  }

  onUpload(event: { files: File[] }): void {
    const sizeResult = filterFilesByMaxSize(event.files, this.maxFileSizeMB);
    if (sizeResult.hasRejected) {
      this.showMaxFileSizeMessage();
    }
    if (!sizeResult.accepted.length) {
      this.fileUpload?.clear();
      return;
    }

    const newFiles = sizeResult.accepted.filter(
      (file) => !this.uploadedFiles.has(file.name),
    );

    newFiles.forEach((file) => {
      this.uploadedFiles.add(file.name);
    });

    newFiles.forEach((file) => {
      this.filesService
        .uploadFile(file)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe({
          next: (response: FileUploadResponse) => {
            this.fileFromServer.push(response.item);
            this.valueAccessor.valueChange(this.fileFromServer);

            if (this.fileUpload) {
              this.fileUpload.clear();
            }

            this.cdr.detectChanges();
          },
          error: (err: unknown) => {
            this.uploadedFiles.delete(file.name);
            const status = err instanceof HttpErrorResponse ? err.status : null;
            if (status === 413) {
              this.showMaxFileSizeMessage();
            }
          },
        });
    });
  }

  private showMaxFileSizeMessage(): void {
    this.api.toastPresent(
      getMaxUploadFileSizeErrorMessage(this.maxFileSizeMB),
      true,
      'Ошибка',
      'error',
    );
  }

  downloadFile(file: FileFromServer): void {
    if (!file) {
      return;
    }

    file.downloadLoading = true;
    const fileName = `${file.original_name}.${file.ext}`;

    this.filesService
      .downloadFile(file.uuid)
      .pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => {
          file.downloadLoading = false;
        }),
      )
      .subscribe({
        next: (blob: Blob) => downloadBlobAsFile(blob, fileName)
      });
  }

  removeFile(file: FileFromServer): void {
    file.removeLoading = true;
    this.filesService
      .removeFile(file.uuid)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (response: FileRemoveResponse) => {
          if (response.success) {
            this.fileFromServer = this.fileFromServer.filter((f) => f.uuid !== file.uuid);

            const fileName = `${file.original_name}.${file.ext}`;
            this.uploadedFiles.delete(fileName);

            if (this.fileUpload) {
              this.fileUpload.clear();
            }

            this.valueAccessor.valueChange(this.fileFromServer);
          } else {
            file.removeLoading = false;
          }
        },
      });
  }
}
